import { Icon } from "@iconify/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/button";
import { cn } from "@/shared/lib/utils";
import type { UploadIdentityCheck } from "@/features/patient/records/api/dataPipelineApi";
import { IDENTITY_REASON_TEXT } from "@/features/empi/empiPresentation";
import { uploadIdentityQuery } from "@/portals/patient/api/patientQueries";
import { QUEUE_PATHS } from "@/features/patient/human-review/reviewQueues";

/**
 * The upload's identity result from Module 5 — the one identity check, same
 * rules for every source (Epic, Athena, C-CDA, AI-read documents). A clean
 * automatic match shows nothing (accepted silently, as everywhere else);
 * anything waiting on the patient links to that item in the Patient Only Queue.
 */
const COPY: Record<Exclude<UploadIdentityCheck["state"], "matched">, { title: string; body: string; icon: string; tone: "wait" | "ok" | "info" }> = {
  checking: {
    title: "Checking this upload is yours",
    body: "We're comparing the details in this upload with your profile.",
    icon: "lucide:loader",
    tone: "info",
  },
  needs_your_confirmation: {
    title: "Please confirm this upload is yours",
    body: "We couldn't confirm the details in this upload match your profile. Its records are on hold until you answer.",
    icon: "lucide:user-search",
    tone: "wait",
  },
  confirmed_by_you: {
    title: "You confirmed this upload is yours",
    body: "Its records are part of your health record.",
    icon: "lucide:check-circle-2",
    tone: "ok",
  },
  not_you: {
    title: "You told us this upload isn't yours",
    body: "Its records are hidden from your health record.",
    icon: "lucide:eye-off",
    tone: "info",
  },
};

/** Loads the upload's identity result live and shows the card when there's something to say. */
export function UploadIdentitySection({ uploadId, actingAsProfileId }: { uploadId: string; actingAsProfileId?: string }) {
  const { data } = useQuery(uploadIdentityQuery(uploadId, actingAsProfileId));
  if (!shouldShowIdentityCard(data)) return null;
  return (
    <div className="mb-2">
      <UploadIdentityCard identity={data} actingAsProfileId={actingAsProfileId} />
    </div>
  );
}

export function shouldShowIdentityCard(identity: UploadIdentityCheck | null | undefined): identity is UploadIdentityCheck {
  return Boolean(identity && identity.state !== "matched");
}

export function UploadIdentityCard({ identity, actingAsProfileId }: { identity: UploadIdentityCheck; actingAsProfileId?: string }) {
  const navigate = useNavigate();
  if (identity.state === "matched") return null;
  const copy = COPY[identity.state];
  const openReview = () => {
    const params = new URLSearchParams();
    if (identity.reviewItemId) params.set("item", identity.reviewItemId);
    if (actingAsProfileId) params.set("profile", actingAsProfileId);
    const query = params.toString();
    navigate(`${QUEUE_PATHS.identity.page}${query ? `?${query}` : ""}`);
  };
  return (
    <section
      data-testid="upload-identity-card"
      aria-label="Identity check"
      className={cn(
        "rounded-xl px-4 py-3",
        copy.tone === "wait" ? "border border-[#F8C9D2] bg-[#FFF5F7]" : "bg-[#F3F4F6]",
      )}
    >
      <div className="flex items-start gap-2">
        <Icon icon={copy.icon} className={cn("mt-0.5 h-4 w-4 shrink-0", copy.tone === "wait" ? "text-[#E4002B]" : copy.tone === "ok" ? "text-[#16A34A]" : "text-[#6B7280]")} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#111827]">Identity Check · {copy.title}</p>
          <p className="mt-0.5 text-xs text-[#6B7280]">{copy.body}</p>
          {identity.state === "needs_your_confirmation" && identity.reasons.length > 0 && (
            <ul className="!mb-0 mt-1.5 grid gap-0.5 !pl-0" data-testid="upload-identity-reasons">
              {identity.reasons.map((reason) => (
                <li key={reason} className="text-xs text-[#9CA3AF]">{IDENTITY_REASON_TEXT[reason] ?? reason}</li>
              ))}
            </ul>
          )}
          {identity.state === "needs_your_confirmation" && (
            <Button
              onClick={openReview}
              className="!mt-2.5 !flex !h-auto !items-center !gap-1.5 !rounded-lg !bg-[#0A2947] !px-3 !py-1.5 !text-xs !font-semibold !text-white hover:!bg-[#0A2947] hover:!opacity-90"
            >
              <Icon icon="lucide:clipboard-pen" className="h-3.5 w-3.5 !text-white" />
              Review in Patient Only Queue
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
